import { Alert } from '@creditornot/cb-alert';
import { Button, TextButton } from '@creditornot/cb-button';
import { CaretDown, CaretUp, Pencil, Plus } from '@creditornot/cb-icons';
import { colors, cbSemanticCssVars } from '@creditornot/cb-ingredients';
import { DateTime, Interval } from 'luxon';
import React, { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { SpecialHoursInputEntry } from 'common/components/weekly-schedules';
import { Typography as T } from 'common/typography';
import time from 'common/utils/time';

type Props = {
  entries: SpecialHoursInputEntry[];
  onEdit?: () => void;
  warning?: string;
};

const PREVIEW_LIST_CUTOFF = 6; // If more entries, use "Preview Mode" with an expand button

const EditButton = styled(Button).attrs({ size: 'small', variant: 'secondary', icon: <Pencil /> })`
  /*
    Absolute so it doesn't mess up layout spacings
  */
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
`;

const Root = styled.div``;

const Header = styled(T.Heading6)`
  color: ${cbSemanticCssVars.textPrimary};
  display: flex;
  justify-content: space-between;
  margin-block-end: 24px;
  position: relative;
`;

const Warning = styled(Alert).attrs({
  size: 'small',
  variant: 'warning',
})`
  margin-block-end: 16px;
`;

const AddButton = styled(Button).attrs({
  variant: 'secondary',
  size: 'small',
})`
  margin: 24px 0 0;
  display: block;
`;

const AddIcon = styled(Plus)`
  fill: ${colors.wolt100};
`;

const Entry = styled.div`
  display: flex;
  margin-block-start: 12px;
  justify-content: space-between;
`;

const Info = styled(T.Body3)`
  display: flex;
  flex-wrap: wrap;

  & > div {
    margin-inline-end: 4px;
  }
`;

const Added = styled(T.Body3)`
  display: flex;
  flex-wrap: wrap;
  color: ${cbSemanticCssVars.textSecondary};
  margin-inline-end: 8px;

  & > span {
    margin-inline-start: 4px;
    text-transform: capitalize;
  }
`;

const Times = styled(T.Body3)`
  display: flex;
  flex-direction: column;
  text-align: end;
  flex-shrink: 0;
`;

const ExpandButton = styled(TextButton).attrs({
  iconPosition: 'end',
  variant: 'brand',
})`
  margin-block-start: 24px;
`;

const dateTimeToString = (dateTime: DateTime) => {
  return dateTime.toLocaleString({ weekday: 'short', year: '2-digit', month: 'numeric', day: 'numeric' });
};

const datesToString = (time: SpecialHoursInputEntry) => {
  if (Interval.fromDateTimes(time.periods[0].start, time.periods[0].end).length('days') === 0) {
    return dateTimeToString(time.periods[0].start);
  }
  return `${dateTimeToString(time.periods[0].start)} – ${dateTimeToString(time.periods[0].end)} `;
};

const SpecialHoursView = ({ entries, onEdit, warning }: Props) => {
  const usePreviewMode = entries.length > PREVIEW_LIST_CUTOFF;

  const [showPreview, setShowPreview] = useState(usePreviewMode);

  const entriesToShow = showPreview ? entries.slice(0, PREVIEW_LIST_CUTOFF) : entries;

  return (
    <Root>
      <Header>
        <FormattedMessage id="venue-details.special-hours" />
        {!!entries?.length && onEdit && (
          <EditButton onClick={onEdit}>
            <FormattedMessage id="state.edit" />
          </EditButton>
        )}
      </Header>
      {warning && <Warning>{warning}</Warning>}
      {entriesToShow.length ? (
        <>
          {entriesToShow.map((entry, i) => (
            <Entry key={i}>
              <Info>
                <div>
                  {datesToString(entry)}
                  {!!entry.message && <>{` (${entry.message}) `}</>}
                </div>
                <Added>
                  <FormattedMessage id="special-hours.added-by" /> <span>{entry.modifiedBy}</span>
                </Added>
              </Info>
              <Times>
                {entry.closedAllDay ? (
                  <FormattedMessage id="opening-hours.closed" />
                ) : (
                  entry.timePairs.map(pair => (
                    <T.Body3 key={pair.startMinutes}>
                      {time.renderMinutesFromMidnight(pair.startMinutes, false)}-
                      {time.renderMinutesFromMidnight(pair.endMinutes, true)}
                    </T.Body3>
                  ))
                )}
              </Times>
            </Entry>
          ))}
          {usePreviewMode && (
            <ExpandButton icon={showPreview ? <CaretDown /> : <CaretUp />} onClick={() => setShowPreview(!showPreview)}>
              <FormattedMessage id={showPreview ? 'special-hours.show-all' : 'special-hours.show-less'} />
            </ExpandButton>
          )}
        </>
      ) : (
        <>
          <T.Body3>
            <FormattedMessage id="venue-details.special-hours-info" />
          </T.Body3>
          {onEdit && (
            <AddButton icon={<AddIcon />} onClick={() => onEdit()}>
              <FormattedMessage id="venue-details.add-special-hours" />
            </AddButton>
          )}
        </>
      )}
    </Root>
  );
};

export default SpecialHoursView;
